/* eslint-disable max-lines */
import React, {ChangeEventHandler, FocusEventHandler, useCallback, useState} from "react";
import {toNumber} from "lodash";
import Textarea from "../../ui/textarea";
import {useFormState} from "../../hooks/useFormState";
import Button from "../../ui/button";
import classes from "../account/AssignModal.module.css";
import {useTranslation} from "react-i18next";
import {
  CustomParameter,
  ParametricResultCondition,
  ParametricResultConditionInput,
  ParametricResultDescription,
  ParametricResultDescriptionInput,
  Maybe,
  Scalars,
  Competence,
} from "../../schema";
import Input from "../../ui/input";
import {AddIcon, DeleteIcon} from "../../ui/icons";
import Select from "../../ui/select";

type contentProps = React.ComponentProps<"div"> & {
  desc?: ParametricResultDescription;
  parameters?: CustomParameter[];
  competencies?: Competence[];
  useCompetences?: boolean;
  singleCondition?: Maybe<Scalars["Boolean"]>;
  onSave?: (data: ParametricResultDescriptionInput) => void;
  onDelete?: () => void;
};

function ParametricResultDescriptionModalHeader() {
  const {t} = useTranslation();
  return <span>{t("components.ParametricResultDescriptionModal.title")}</span>;
}

function ParametricResultDescriptionModalContent({
  desc,
  parameters,
  competencies,
  useCompetences,
  singleCondition,
  onSave,
  onDelete,
}: contentProps) {
  const {t} = useTranslation();

  const formState = useFormState<{
    conditions: ParametricResultConditionInput[];
    message: string;
  }>({
    initialValues: {
      conditions:
        desc?.conditions?.map((c) => ({
          parameterId: c.parameter?.id ?? undefined,
          competenceId: c.competence?.id ?? undefined,
          minValue: c.minValue,
          maxValue: c.maxValue,
        })) ?? [],
      message: desc?.message ?? "",
    },

    preventDefault: true,

    onSubmit: (data) => {
      data.conditions = [
        ...data.conditions.map((condition) => ({
          parameterId: condition.parameterId,
          competenceId: condition.competenceId,
          minValue: condition.minValue,
          maxValue: condition.maxValue,
        })),
      ];
      onSave && onSave(data);
    },
  });

  const [conditionsList, setConditionsList] = useState<ParametricResultCondition[]>(desc?.conditions ?? []);
  const mapConditions = useCallback(
    (conds: ParametricResultCondition[]) =>
      conds.map((c) => ({
        parameterId: useCompetences ? undefined : c.parameter?.id,
        competenceId: useCompetences ? c.competence?.id : undefined,
        minValue: c.minValue,
        maxValue: c.maxValue,
      })),
    [useCompetences],
  );

  const onMinChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const index = toNumber(e.currentTarget.getAttribute("data-index")!);
      const value = toNumber(e.currentTarget.value);
      const temp = [...conditionsList];
      temp[index] = {...temp[index], minValue: value};
      setConditionsList(temp);
      formState.setValues(() => ({conditions: mapConditions(temp)}));
    },
    [formState, conditionsList, mapConditions],
  );

  const onMaxChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const index = toNumber(e.currentTarget.getAttribute("data-index")!);
      const value = toNumber(e.currentTarget.value);
      const temp = [...conditionsList];
      temp[index] = {...temp[index], maxValue: value};
      setConditionsList(temp);
      formState.setValues(() => ({conditions: mapConditions(temp)}));
    },
    [formState, conditionsList, mapConditions],
  );

  const onRangeNumberFocus = useCallback<FocusEventHandler<HTMLInputElement>>((e) => {
    if (e.currentTarget.value === "0") {
      e.currentTarget.select();
    }
  }, []);

  const onParameterChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    (e) => {
      const index = toNumber(e.currentTarget.getAttribute("data-index")!);
      const value = e.currentTarget.value;
      const temp = [...conditionsList];
      temp[index] = {
        ...(useCompetences
          ? {competence: competencies?.find((c) => c.id === value) ?? null}
          : {parameter: parameters?.find((p) => p.id === value) ?? null}),
        minValue: temp[index].minValue,
        maxValue: temp[index].maxValue,
      };
      setConditionsList(temp);
      formState.setValues(() => ({conditions: mapConditions(temp)}));
    },
    [formState, conditionsList, parameters, competencies, useCompetences, mapConditions],
  );

  const onAddConditionButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const newCondition: ParametricResultCondition = useCompetences
        ? {competence: null, minValue: 0, maxValue: 0}
        : {parameter: null, minValue: 0, maxValue: 0};
      const temp = [...conditionsList, newCondition];
      setConditionsList(temp);
      formState.setValues(() => ({conditions: mapConditions(temp)}));
    },
    [conditionsList, formState, useCompetences, mapConditions],
  );

  const onDeleteConditionButtonClick = useCallback(
    (e: React.MouseEvent) => {
      const index = toNumber(e.currentTarget.getAttribute("data-index")!);
      e.stopPropagation();
      e.preventDefault();
      const temp = [...conditionsList];
      temp.splice(index, 1);
      setConditionsList(temp);
      formState.setValues(() => ({conditions: mapConditions(temp)}));
    },
    [conditionsList, formState, mapConditions],
  );

  const submitDisabled = undefined;

  return (
    <form className={classes.root} method="post" onSubmit={formState.submitHandler}>
      <>
        <p className={classes.helpText}>{t("components.ParametricResultDescriptionModal.conditionsHelp")}</p>
        {conditionsList.map((condition, index) => (
          <>
            <Select
              data-index={index}
              value={(useCompetences ? condition.competence?.id : condition.parameter?.id) ?? ""}
              onChange={onParameterChange}
            >
              <option value={""}>
                {useCompetences
                  ? t("components.ParametricResultDescriptionModal.competenceNotSelected")
                  : t("components.ParametricResultDescriptionModal.notSelected")}
              </option>
              {useCompetences && competencies
                ? competencies.map((competence) => (
                    <option key={competence.id} value={competence.id}>
                      {competence.name}
                    </option>
                  ))
                : parameters?.map((param) => (
                    <option key={param.id} value={param.id}>
                      {param.name}
                    </option>
                  ))}
            </Select>
            <div style={{display: "flex", flexDirection: "row", alignItems: "baseline"}}>
              {t("components.ParametricResultDescriptionModal.from")}
              <Input
                data-index={index}
                style={{width: "70px", height: "40px", margin: "12px 12px 20px 12px"}}
                required
                type="number"
                value={condition.minValue ?? 0}
                onChange={onMinChange}
                onFocus={onRangeNumberFocus}
              />
              {t("components.ParametricResultDescriptionModal.to")}
              <Input
                data-index={index}
                style={{width: "70px", height: "40px", margin: "12px 12px 20px 12px"}}
                required
                type="number"
                value={condition.maxValue ?? 0}
                onChange={onMaxChange}
                onFocus={onRangeNumberFocus}
              />
              <Button data-index={index} size="s" onClick={onDeleteConditionButtonClick}>
                <DeleteIcon fontSize={14} />
              </Button>
            </div>
          </>
        ))}
        {!(formState.values.conditions.length > 0 && singleCondition) && (
          <Button style={{marginBottom: "28px"}} color="secondary" onClick={onAddConditionButtonClick}>
            <AddIcon style={{marginRight: "4px"}} />
            {t("common.add")}
          </Button>
        )}

        <p className={classes.helpText}>{t("components.ResultDescriptionModal.messageHelpText")}</p>
        <Textarea
          className={classes.input}
          name="message"
          value={formState.values.message}
          rows={8}
          placeholder={t("components.ResultDescriptionModal.messagePlaceholder")}
          onChange={formState.changeHandler}
        />
      </>

      <div className={classes.bottom}>
        <Button type="submit" color={submitDisabled ? "secondary" : "success"} disabled={submitDisabled}>
          {t("components.ResultDescriptionModal.submit")}
        </Button>
        {desc && (
          <Button type="button" color="danger" onClick={onDelete}>
            {t("components.ResultDescriptionModal.delete")}
          </Button>
        )}
      </div>
    </form>
  );
}

const ParametricResultDescriptionModal = {
  Header: ParametricResultDescriptionModalHeader,
  Content: ParametricResultDescriptionModalContent,
};

export default ParametricResultDescriptionModal;
